import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AdminTableSkeleton } from "../../components/admin/AdminTableSkeleton";
import { SupportMessagePanel } from "../../components/admin/support/SupportMessagePanel";
import {
  SUPPORT_MESSAGE_HEADERS,
  SupportMessagesPager,
  SupportMessagesTable,
} from "../../components/admin/support/SupportMessagesTable";
import { type UseAdminSupportMessagesResult, useAdminSupportMessages } from "../../hooks/useAdminSupportMessages";
import styles from "../AdminInstances.module.css";
import own from "../../components/admin/support/SupportMessages.module.css";
import { AdminPageIntro } from "../../components/admin/AdminPageIntro";

// What tenant agents sent to support through the gateway's ZTC_SEND_SUPPORT_MESSAGE tool.
// The open message (?id=) and the page (?before=) live in the URL so they can be linked to.
export const AdminSupportMessages = (): JSX.Element => {
  const [params, setParams] = useSearchParams();
  const tenantId = params.get("tenantId");
  const selectedId = params.get("id");
  const before = params.get("before");
  const [unreviewedOnly, setUnreviewedOnly] = useState<boolean>(false);
  const list = useAdminSupportMessages({ tenantId, unreviewedOnly, before });

  const setParam = (changes: Record<string, string | null>): void =>
    setParams((prev) => {
      for (const [key, value] of Object.entries(changes)) {
        if (value === null) prev.delete(key);
        else prev.set(key, value);
      }
      return prev;
    });

  return (
    <div className={styles.page}>
      <AdminPageIntro title="Support">
        Copies of the housekeeping reports and tool-limit notices agents send their owners, and the issues with our plugins that agents and people report. Customers don&apos;t see this page.
      </AdminPageIntro>

      <Filters
        unreviewedOnly={unreviewedOnly}
        tenantId={tenantId}
        onUnreviewedOnly={(next) => {
          setUnreviewedOnly(next);
          setParam({ before: null });
        }}
        onAllInstances={() => setParam({ tenantId: null, before: null })}
      />

      <ListBody
        list={list}
        selectedId={selectedId}
        paged={before !== null}
        onOpen={(id) => setParam({ id })}
        onOlder={(lastId) => setParam({ before: lastId })}
        onNewest={() => setParam({ before: null })}
      />

      {selectedId !== null && (
        <SupportMessagePanel
          key={selectedId}
          id={selectedId}
          summary={list.messages.find((m) => m.id === selectedId) ?? null}
          onClose={() => setParam({ id: null })}
          onReview={list.markReviewed}
        />
      )}
    </div>
  );
};

type FiltersProps = {
  readonly unreviewedOnly: boolean;
  readonly tenantId: string | null;
  readonly onUnreviewedOnly: (next: boolean) => void;
  readonly onAllInstances: () => void;
};

const Filters = ({ unreviewedOnly, tenantId, onUnreviewedOnly, onAllInstances }: FiltersProps): JSX.Element => (
  <div className={own.filters}>
    <label>
      <input type="checkbox" checked={unreviewedOnly} onChange={(e) => onUnreviewedOnly(e.target.checked)} />{" "}
      Unreviewed only
    </label>
    {tenantId !== null && (
      <span>
        Instance <code>{tenantId}</code>{" "}
        <button type="button" className={styles.secondaryBtn} onClick={onAllInstances}>
          Show all instances
        </button>
      </span>
    )}
  </div>
);

type ListBodyProps = {
  readonly list: UseAdminSupportMessagesResult;
  readonly selectedId: string | null;
  readonly paged: boolean;
  readonly onOpen: (id: string) => void;
  readonly onOlder: (lastId: string) => void;
  readonly onNewest: () => void;
};

const ListBody = ({ list, selectedId, paged, onOpen, onOlder, onNewest }: ListBodyProps): JSX.Element => (
  <section className={styles.listSection} style={{ opacity: list.isFetching ? 0.6 : 1 }}>
    {list.status === "loading" && (
      <AdminTableSkeleton headers={SUPPORT_MESSAGE_HEADERS} rows={6} tableClassName={styles.table ?? ""} />
    )}
    {list.status === "error" && <div className={styles.empty}>{list.error ?? "Failed to load support messages."}</div>}
    {list.status === "ready" && list.messages.length === 0 && (
      <div className={styles.empty} data-testid="support-messages-empty">
        No support messages.
      </div>
    )}
    {list.status === "ready" && list.messages.length > 0 && (
      <SupportMessagesTable messages={list.messages} selectedId={selectedId} onOpen={onOpen} />
    )}
    {list.status === "ready" && (
      <SupportMessagesPager messages={list.messages} paged={paged} onOlder={onOlder} onNewest={onNewest} />
    )}
  </section>
);
